import WebApp from '@twa-dev/sdk';

import {
  getCloudStorageValue,
  removeCloudStorageValue,
} from '@/shared/helpers/telegram/cloud-storage';
import { type StartAppArgs } from '@/shared/stores/app-store/types';

import {
  DEFAULT_LOCALE,
  LS_APP_LOCALE_KEY,
  LS_FORCE_LOCALE_KEY,
  SUPPORTED_LOCALES,
} from '../constants';
import { Languages, type Locale } from '../types';
import { isValidLocale } from './is-valid-locale';

// CIS countries that should default to Russian
const CIS_COUNTRIES = [
  'ru', // Russia
  'by', // Belarus
  'kz', // Kazakhstan
  'kg', // Kyrgyzstan
  'tj', // Tajikistan
  'uz', // Uzbekistan
  'am', // Armenia
  'az', // Azerbaijan
  'ge', // Georgia
  'md', // Moldova
  'ua', // Ukraine
];

/**
 * Determines if the user is from a CIS region based on Telegram or browser locale
 */
const isCISRegion = (tgLocale?: string, browserLocale?: string): boolean => {
  const tgCountry = tgLocale?.split('-')[1]?.toLowerCase();
  const browserCountry = browserLocale?.split('-')[1]?.toLowerCase();

  return (
    CIS_COUNTRIES.includes(tgCountry || '') ||
    CIS_COUNTRIES.includes(browserCountry || '') ||
    CIS_COUNTRIES.includes(tgLocale?.toLowerCase() || '') ||
    CIS_COUNTRIES.includes(browserLocale?.split('-')[0]?.toLowerCase() || '')
  );
};

const setLocale = (locale: string) => {
  localStorage.setItem(LS_APP_LOCALE_KEY, locale);
};

const makeLocale = (locale: string) => {
  return {
    game: locale,
    app: SUPPORTED_LOCALES.includes(locale as Languages)
      ? (locale as Languages)
      : Languages.en,
  };
};

/**
 * Determines the user's locale based on priority:
 * 1. Market locale (from startAppArgs.game.locale) - highest priority, from market
 * 2. Forced locale (temporary override from CloudStorage)
 * 3. Telegram user language
 * 4. Browser/OS language
 * 5. Previously stored locale
 * 6. Default locale (Russian for CIS, English otherwise)
 */
export const getLocale = async (startAppArgs?: StartAppArgs): Promise<Locale> => {
  // 1. Check market locale (from startAppArgs) - highest priority
  const marketLocale = startAppArgs?.locale;
  if (marketLocale && isValidLocale(marketLocale.toLowerCase())) {
    const gameLocale = marketLocale.toLowerCase();
    // Store market language as user preference
    setLocale(gameLocale);
    return makeLocale(gameLocale);
  }

  // 2. Check forced locale (temporary override)
  const forcedLocale = (await getCloudStorageValue(LS_FORCE_LOCALE_KEY))?.toLowerCase();
  if (forcedLocale && isValidLocale(forcedLocale)) {
    // Remove forced locale after use (one-time override)
    removeCloudStorageValue(LS_FORCE_LOCALE_KEY);
    return makeLocale(forcedLocale);
  }
  // Always remove forced locale key even if invalid
  if (forcedLocale) {
    removeCloudStorageValue(LS_FORCE_LOCALE_KEY);
  }

  // 3. Check Telegram user language
  const tgLocale = WebApp?.initDataUnsafe?.user?.language_code?.toLowerCase();
  if (tgLocale && isValidLocale(tgLocale)) {
    setLocale(tgLocale);
    return makeLocale(tgLocale);
  }

  // 4. Check browser/OS language
  const browserLocale = window.navigator.language;
  const osLoc = browserLocale.split('-')[0]?.toLowerCase();
  if (osLoc && isValidLocale(osLoc)) {
    setLocale(osLoc);
    return makeLocale(osLoc);
  }

  // 5. Check stored locale
  const storedLocale = localStorage.getItem(LS_APP_LOCALE_KEY)?.toLowerCase();
  if (storedLocale && isValidLocale(storedLocale)) {
    return makeLocale(storedLocale);
  }

  // 6. Use smart default based on region
  const isCIS = isCISRegion(tgLocale, browserLocale);
  const gameLocale = isCIS ? Languages.ru : DEFAULT_LOCALE;
  setLocale(gameLocale);

  return makeLocale(gameLocale);
};
